import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { freshCart, validSections, sectionContext } from './avl-cart-state.js';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #drawerLock = null;
  #drawerHost = null;
  #drawerItems = null;
  #drawerBusy = false;
  #drawerIntent = null;
  #drawerFocus = null;

  #currentDrawer() {
    return this.#drawerHost?.querySelector('cart-items-component[data-drawer]') || this.#drawerItems;
  }

  #drawerStatus(message, error = false) {
    const items = this.#currentDrawer();
    let status = items?.querySelector('[data-avlc-cart-status]');
    if (!status && items) {
      status = document.createElement('div');
      status.dataset.avlcCartStatus = '';
      status.className = 'avlc-cart__status';
      items.prepend(status);
    }
    if (status) {
      status.hidden = false;
      status.setAttribute('role', error ? 'alert' : 'status');
      status.textContent = message;
    }
    return status;
  }

  #setDrawerDisabled(disabled) {
    const items = this.#currentDrawer();
    if (!items) return;
    items.classList.toggle('cart-items-disabled', disabled);
    items.setAttribute('aria-busy', String(disabled));
    items.querySelectorAll('.avlc-cart__line-actions button,.avlc-cart__line-actions input,.cart-items__quantity button,.cart-items__quantity input,.cart__ctas button,cart-discount-component button,cart-discount-component input').forEach(control => {
      if (disabled && !control.disabled) {
        control.dataset.avlDiscountEnabled = '';
        control.disabled = true;
      } else if (!disabled && control.hasAttribute('data-avl-discount-enabled')) {
        control.disabled = control.dataset.avlUnavailable === 'true';
        control.removeAttribute('data-avl-discount-enabled');
      }
    });
    items.querySelectorAll('.additional-checkout-buttons').forEach(control => { control.inert = disabled; });
    if (!disabled) items.querySelectorAll('cart-quantity-selector-component').forEach(control => control.updateButtonStates?.());
  }

  #restoreDrawerFocus(value, invalid = false) {
    const items = this.#currentDrawer();
    const component = items?.querySelector('cart-discount-component');
    if (!component) return;
    const input = component.querySelector('input[name="discount"]');
    const details = component.closest('details');
    if (details && this.#drawerFocus?.open) details.open = true;
    if (input) {
      input.value = value;
      if (invalid) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
    const dialog = items.closest('dialog');
    if (!dialog || dialog.open) {
      const target = this.#drawerFocus?.submit && !invalid ? component.querySelector('button[type="submit"]') : input;
      target?.focus({ preventScroll: true });
    }
    const content = items.querySelector('.cart-drawer__content');
    const summary = items.querySelector('.cart-drawer__summary');
    if (content) content.scrollTop = this.#drawerFocus?.contentScroll || 0;
    if (summary) summary.scrollTop = this.#drawerFocus?.summaryScroll || 0;
  }

  #confirmedDrawerCart(cart, ids) {
    return Number.isInteger(cart?.total_price) && Array.isArray(cart?.items) &&
      Array.isArray(cart?.discount_codes) && validSections(cart, ids);
  }

  async #finishDrawerDiscount(cart, reconciled = false) {
    const intent = this.#drawerIntent;
    const applied = cart.discount_codes.some(discount => discount.code?.toLowerCase() === intent.code.toLowerCase() && discount.applicable === true);
    const successful = intent.remove ? !applied : applied;
    const items = this.#currentDrawer();
    if (!items?.renderConfirmedCart) throw new Error('Cart renderer unavailable');
    // Keep the mutation lock until header hydration has actually completed.
    await items.renderConfirmedCart(cart);
    // Dispatch from the rendered host so its cart handler does not morph twice.
    items.dispatchEvent(new CartUpdateEvent(cart, this.id || 'cart-discount', {
      source: 'cart-discount-component', itemCount: cart.item_count, sections: cart.sections,
    }));
    if (globalThis.__avlCartMutation === this.#drawerLock) delete globalThis.__avlCartMutation;
    this.#drawerLock = null;
    this.#setDrawerDisabled(false);
    const message = successful
      ? (intent.remove ? 'Rabatkoden er fjernet. Din kurv er opdateret.' : 'Rabatkoden er tilføjet. Din kurv er opdateret.')
      : (intent.remove ? 'Rabatkoden kunne ikke fjernes. Din aktuelle kurv er vist. Prøv igen.'
        : reconciled ? 'Rabatkoden blev ikke tilføjet. Din aktuelle kurv er vist. Kontrollér koden, eller prøv igen.'
          : 'Rabatkoden kan ikke bruges til denne kurv. Kontrollér koden og betingelserne.');
    this.#drawerStatus(message, !successful);
    this.#restoreDrawerFocus(!successful && !intent.remove ? intent.code : '', !successful && !intent.remove);
  }

  #drawerRecovery(message) {
    this.#setDrawerDisabled(true);
    const status = this.#drawerStatus(message, true);
    if (!status) return;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button';
    retry.textContent = 'Hent kurven igen';
    retry.addEventListener('click', () => this.#retryDrawerRead());
    status.append(retry);
    const dialog = this.#currentDrawer()?.closest('dialog');
    if (!dialog || dialog.open) retry.focus({ preventScroll: true });
  }

  async #retryDrawerRead() {
    if (this.#drawerBusy || globalThis.__avlCartMutation !== this.#drawerLock) return;
    this.#drawerBusy = true;
    this.#drawerStatus('Henter din kurv…');
    try {
      const ids = [this.#currentDrawer().dataset.sectionId];
      const cart = await freshCart(ids);
      if (!this.#confirmedDrawerCart(cart, ids)) throw new Error('Incomplete cart read');
      await this.#finishDrawerDiscount(cart, true);
    } catch {
      this.#drawerRecovery('Kurven kunne ikke hentes. Prøv igen, når forbindelsen er tilbage.');
    } finally {
      this.#drawerBusy = false;
    }
  }

  async #updateDrawerDiscount(code, remove = false) {
    const items = this.closest('cart-items-component[data-drawer]');
    if (!items?.dataset.sectionId) return;
    this.#drawerItems = items;
    this.#drawerHost = items.closest('cart-drawer-component');
    if (globalThis.__avlCartMutation || this.#drawerBusy) {
      this.#drawerStatus('Kurven opdateres. Vent et øjeblik.');
      return;
    }
    const existing = this.#existingDiscounts();
    if (!code || (!remove && existing.some(value => value.toLowerCase() === code.toLowerCase()))) {
      this.#drawerStatus(code ? 'Rabatkoden er allerede tilføjet.' : 'Skriv en rabatkode.', !code);
      return;
    }
    this.#drawerIntent = { code, remove };
    this.#drawerFocus = {
      open: Boolean(this.closest('details')?.open),
      submit: document.activeElement?.matches('button[type="submit"]'),
      contentScroll: items.querySelector('.cart-drawer__content')?.scrollTop || 0,
      summaryScroll: items.querySelector('.cart-drawer__summary')?.scrollTop || 0,
    };
    this.#drawerLock = {};
    globalThis.__avlCartMutation = this.#drawerLock;
    this.#drawerBusy = true;
    this.#setDrawerDisabled(true);
    this.#drawerStatus(remove ? 'Fjerner rabatkoden…' : 'Kontrollerer rabatkoden…');
    Object.values(this.refs).forEach(ref => ref instanceof HTMLElement && ref.classList.add('hidden'));
    const ids = [items.dataset.sectionId];
    const discounts = remove ? existing.filter(value => value.toLowerCase() !== code.toLowerCase()) : [...existing, code];
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: discounts.join(','), sections: ids, sections_url: sectionContext() }),
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || !this.#confirmedDrawerCart(cart, ids)) throw new Error('Discount update needs reconciliation');
      await this.#finishDrawerDiscount(cart);
    } catch {
      // The POST may have succeeded despite a lost response. Read; never replay it.
      try {
        const cart = await freshCart(ids);
        if (!this.#confirmedDrawerCart(cart, ids)) throw new Error('Incomplete cart read');
        await this.#finishDrawerDiscount(cart, true);
      } catch {
        this.#drawerRecovery('Vi kunne ikke bekræfte rabatten. Hent kurven igen, før du fortsætter.');
      }
    } finally {
      clearTimeout(timer);
      this.#drawerBusy = false;
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    if (this.closest('cart-items-component[data-drawer]')) {
      await this.#updateDrawerDiscount(discountCodeValue.trim());
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.closest('cart-items-component[data-drawer]')) {
      const pill = event.target instanceof Element ? event.target.closest('.cart-discount__pill') : null;
      if (pill?.dataset.discountCode) await this.#updateDrawerDiscount(pill.dataset.discountCode, true);
      return;
    }

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
